import React, { useState, useEffect } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { adminAnnouncementsAPI } from '../../utils/api';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../ui/toaster';

const KINDS = ['Evento', 'Promoción', 'Nuevo socio', 'Mantenimiento', 'Aviso'];

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function relativeDate(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 60) return `hace ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `hace ${d} d`;
  return new Date(dateStr).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
}

function KindBadge({ kind }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '3px 10px', borderRadius: 9999,
      fontSize: 11, fontWeight: 600,
      background: '#F5F5F7', color: '#0A0A0A',
    }}>
      {kind}
    </span>
  );
}

const AnnouncementsManagement = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [image, setImage] = useState('');
  const [kind, setKind] = useState('Evento');
  const [sendPush, setSendPush] = useState(true);

  useEffect(() => { loadAnnouncements(); }, []);

  const loadAnnouncements = async () => {
    try {
      setLoading(true);
      const res = await adminAnnouncementsAPI.getAll();
      setAnnouncements(res.data || []);
    } catch (err) {
      toast({ title: 'Error', description: err.message || 'No se pudieron cargar los avisos', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!title.trim() || !body.trim()) {
      toast({ title: 'Campos requeridos', description: 'Agrega título y contenido.', variant: 'destructive' });
      return;
    }
    try {
      setSaving(true);
      await adminAnnouncementsAPI.create({ title, body, image_url: image || null, is_active: true, kind, send_push: sendPush, publish_now: false });
      toast({ title: 'Borrador guardado' });
      setTitle(''); setBody(''); setImage('');
      loadAnnouncements();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!title.trim() || !body.trim()) {
      toast({ title: 'Campos requeridos', description: 'Agrega título y contenido.', variant: 'destructive' });
      return;
    }
    try {
      setSaving(true);
      await adminAnnouncementsAPI.create({ title, body, image_url: image || null, is_active: true, kind, send_push: sendPush, publish_now: true });
      toast({ title: 'Aviso publicado', description: `"${title}" ya es visible para los miembros.` });
      setTitle(''); setBody(''); setImage('');
      loadAnnouncements();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handlePublishExisting = async (id) => {
    try {
      await adminAnnouncementsAPI.publish(id);
      toast({ title: 'Publicado' });
      loadAnnouncements();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const handleToggleActive = async (a) => {
    try {
      await adminAnnouncementsAPI.update(a.id, { is_active: !a.is_active });
      loadAnnouncements();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este aviso?')) return;
    try {
      await adminAnnouncementsAPI.delete(id);
      setAnnouncements(prev => prev.filter(a => a.id !== id));
      toast({ title: 'Eliminado' });
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const published = announcements.filter(a => a.published_at);
  const drafts = announcements.filter(a => !a.published_at);

  return (
    <div style={{ padding: 24 }}>
      <Toaster />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 16 }}>

        {/* ── Left: draft editor ─────────────────────────── */}
        <div style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.06)', borderRadius: 16, padding: 18 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#A1A1A6', marginBottom: 14 }}>
            Borrador · nuevo aviso
          </div>

          {/* Title */}
          <input
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="Título del aviso…"
            style={{
              width: '100%', height: 48, padding: '0 14px',
              border: '1.5px solid rgba(0,0,0,0.08)', borderRadius: 10,
              fontSize: 15, fontWeight: 600, fontFamily: 'Inter, system-ui, sans-serif',
              letterSpacing: '-0.01em', marginBottom: 10,
              outline: 'none', boxSizing: 'border-box',
            }}
          />

          {/* Body */}
          <textarea
            value={body}
            onChange={e => setBody(e.target.value)}
            placeholder="Escribe el contenido del aviso…"
            rows={5}
            style={{
              width: '100%', padding: '12px 14px',
              border: '1.5px solid rgba(0,0,0,0.08)', borderRadius: 10,
              fontSize: 13.5, fontFamily: 'Inter, system-ui, sans-serif',
              lineHeight: 1.55, resize: 'vertical',
              outline: 'none', boxSizing: 'border-box',
            }}
          />

          {/* Imagen / logo del aviso */}
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Imagen del aviso (opcional)</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {image ? (
                <img src={image} alt="preview" style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 10, border: '1px solid rgba(0,0,0,0.08)' }} />
              ) : (
                <div style={{ width: 64, height: 64, borderRadius: 10, background: '#F5F5F7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: '#A1A1A6' }}>Sin imagen</div>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  if (f) { try { setImage(await fileToBase64(f)); } catch {} }
                }}
                style={{ fontSize: 12 }}
              />
              {image && (
                <button onClick={() => setImage('')} style={{ fontSize: 12, color: '#A1A1A6', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>quitar</button>
              )}
            </div>
          </div>

          {/* Kind pills */}
          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            {KINDS.map(k => (
              <button
                key={k}
                onClick={() => setKind(k)}
                style={{
                  padding: '5px 12px', borderRadius: 9999,
                  fontSize: 11.5, fontWeight: 600,
                  background: kind === k ? '#0A0A0A' : '#F5F5F7',
                  color: kind === k ? '#fff' : '#0A0A0A',
                  border: 'none', cursor: 'pointer',
                }}
              >
                {k}
              </button>
            ))}
          </div>

          {/* Push toggle */}
          <div style={{
            marginTop: 18, padding: 14, borderRadius: 12, background: '#F5F5F7',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600 }}>Notificación push</div>
              <div style={{ fontSize: 11, color: '#6B6B70' }}>Enviar al publicar a todos los dispositivos.</div>
            </div>
            <button
              onClick={() => setSendPush(v => !v)}
              style={{
                position: 'relative', width: 36, height: 20,
                background: sendPush ? '#0A0A0A' : 'rgba(0,0,0,0.15)',
                borderRadius: 9999, border: 'none', cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              <span style={{
                position: 'absolute', width: 16, height: 16, background: '#fff',
                borderRadius: '50%', top: 2, left: sendPush ? 18 : 2,
                transition: 'left .18s',
              }} />
            </button>
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button
              onClick={handleSaveDraft}
              disabled={saving}
              style={{
                flex: 1, height: 42, borderRadius: 9,
                background: '#fff', border: '1px solid rgba(0,0,0,0.08)',
                fontWeight: 600, fontSize: 13, cursor: saving ? 'not-allowed' : 'pointer',
              }}
            >
              {saving ? 'Guardando…' : 'Guardar borrador'}
            </button>
            <button
              onClick={handlePublish}
              disabled={saving}
              style={{
                flex: 1, height: 42, borderRadius: 9,
                background: '#0A0A0A', color: '#fff',
                fontWeight: 600, fontSize: 13,
                border: 'none', cursor: saving ? 'not-allowed' : 'pointer',
              }}
            >
              Publicar ahora
            </button>
          </div>
        </div>

        {/* ── Right: published + drafts list ─────────────── */}
        <div>
          {/* Drafts */}
          {drafts.length > 0 && (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#A1A1A6', marginBottom: 10 }}>
                Borradores
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
                {drafts.map(a => (
                  <div key={a.id} style={{ background: '#fff', border: '1.5px dashed rgba(0,0,0,0.10)', borderRadius: 14, padding: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <KindBadge kind={a.kind} />
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          onClick={() => handlePublishExisting(a.id)}
                          style={{ height: 28, padding: '0 10px', borderRadius: 7, background: '#0A0A0A', color: '#fff', fontSize: 11, fontWeight: 600, border: 'none', cursor: 'pointer' }}
                        >
                          Publicar
                        </button>
                        <button
                          onClick={() => handleDelete(a.id)}
                          style={{ height: 28, width: 28, borderRadius: 7, background: '#F5F5F7', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                          <Trash2 size={12} color="#A1A1A6" />
                        </button>
                      </div>
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600, marginTop: 8 }}>{a.title}</div>
                    <div style={{ fontSize: 11.5, color: '#6B6B70', marginTop: 4, lineHeight: 1.5 }}>{a.body}</div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Published */}
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#A1A1A6', marginBottom: 10 }}>
            Publicados
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', color: '#A1A1A6', padding: '24px 0' }}>Cargando…</div>
          ) : published.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#A1A1A6', padding: '24px 0', fontSize: 13 }}>
              No hay avisos publicados aún.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {published.map(a => (
                <div key={a.id} style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.06)', borderRadius: 14, padding: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <KindBadge kind={a.kind} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <button
                        onClick={() => handleToggleActive(a)}
                        title={a.is_active ? 'Visible para miembros — clic para ocultar' : 'Oculto — clic para activar'}
                        style={{
                          height: 24, padding: '0 10px', borderRadius: 9999, fontSize: 11, fontWeight: 600,
                          border: 'none', cursor: 'pointer',
                          background: a.is_active ? '#0A0A0A' : '#F5F5F7',
                          color: a.is_active ? '#fff' : '#A1A1A6',
                        }}
                      >
                        {a.is_active ? 'Activo' : 'Oculto'}
                      </button>
                      <span style={{ fontSize: 11, color: '#A1A1A6' }}>{relativeDate(a.published_at)}</span>
                      <button
                        onClick={() => handleDelete(a.id)}
                        style={{ height: 26, width: 26, borderRadius: 6, background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      >
                        <Trash2 size={12} color="#C4C4C9" />
                      </button>
                    </div>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, marginTop: 8 }}>{a.title}</div>
                  <div style={{ fontSize: 11.5, color: '#6B6B70', marginTop: 4, lineHeight: 1.5 }}>{a.body}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnnouncementsManagement;
